import React, { useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Col } from '@opendoor/atoms/box';
import { useRequiredFormContext } from '@opendoor/atoms/field';
import { Form } from '@opendoor/atoms/form';
import { Navigate } from 'react-router';

import { ERROR_PAGE_ROUTE } from '../../src/routes/routes';
import { GetPartnerOptionsDocument } from '../graphql/queries/__generated__/GetPartnerOptions';

type PartnerOptionsProps = {
  /** Partnership type the option list is scoped to. */
  partnershipType: string;
  /** Identifier carried in from the sign-up link, preselected once options load. */
  initialPartnerIdentifier?: string;
  /** Label for the select. */
  title: string;
  /** Placeholder shown before a partner is chosen. */
  placeholder: string;
  /** Whether the free-text "Other" field is required and rendered. */
  showOtherField: boolean;
  /** Reports the query's loading state to the page, which owns the page loader. */
  showLoader: (isLoading: boolean) => void;
};

const PartnerOptions: React.FC<PartnerOptionsProps> = (props: PartnerOptionsProps) => {
  const form = useRequiredFormContext();

  const { loading, error, data } = useQuery(GetPartnerOptionsDocument, {
    variables: { partnershipType: props.partnershipType },
  });

  useEffect(() => {
    props.showLoader(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const partnerOptions = useMemo(() => {
    return (
      data?.signupPartnerOptions
        .map((partner) => ({
          value: partner.identifier,
          label: partner.identifier === 'other_builder' ? 'Other' : partner.displayName,
        }))
        .sort((partnerA, partnerB) => (partnerA.label > partnerB.label ? 1 : -1)) || []
    );
  }, [data]);

  const { initialPartnerIdentifier } = props;
  useEffect(() => {
    // Waiting for the query keeps the pass-through below from claiming the field
    // with a raw identifier before the option it matches has loaded.
    if (!initialPartnerIdentifier || !data || form.getFieldValue('partner')) {
      return;
    }
    const match = partnerOptions.find(
      (partner) => partner.value.toLowerCase() === initialPartnerIdentifier.toLowerCase(),
    );
    // An identifier missing from the option list still carries the link's
    // attribution — a partner delisted from `signupPartnerOptions` can have
    // active branches — so pass it through rather than dropping the referral.
    form.setFieldValue(
      'partner',
      match ?? { value: initialPartnerIdentifier, label: initialPartnerIdentifier },
    );
  }, [initialPartnerIdentifier, data, partnerOptions, form]);

  if (error) {
    return <Navigate to={ERROR_PAGE_ROUTE} replace />;
  }

  return loading ? null : (
    <Col className="pb-10">
      <Form.Combobox
        name="partner"
        label={props.title}
        placeholder={props.placeholder}
        options={partnerOptions}
        analyticsName="partner-options-dropdown"
      />
      {props.showOtherField && (
        <Col className="pt-4">
          <Form.Input
            name="otherPartner"
            label="Other"
            placeholder="Please specify"
            className="w-full"
            analyticsName="partnership-dashboard-fe-other-partner-input"
          />
        </Col>
      )}
    </Col>
  );
};

export default PartnerOptions;
